import { Component, EventEmitter, Input, Output, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { QuillModule } from 'ngx-quill';
import { PhotoboothService } from '../../services/photobooth.service';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

@Component({
  selector: 'app-device-app-release-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  templateUrl: './device-app-release-modal.component.html',
  styleUrls: ['./device-app-release-modal.component.css']
})
export class DeviceAppReleaseModalComponent implements OnInit {
  @Input() deviceId!: string;
  @Input() deviceName!: string;
  @Input() currentAppVersion?: string;
  @Output() close = new EventEmitter<void>();
  @Output() released = new EventEmitter<void>();

  private photoboothService = inject(PhotoboothService);
  private firestore = inject(Firestore);
  private sanitizer = inject(DomSanitizer);

  appVersionsList: any[] = [];
  selectedVersion: any = null;
  triggerRestart: boolean = true;
  forceUpdate: boolean = false;
  isLoading: boolean = false;
  isPublishing: boolean = false;
  statusMessage: string = '';
  errorMessage: string = '';

  deviceTargetVersion?: string;
  deviceUpdateNeeded: boolean = false;

  // Release Note Drawer State
  showReleaseNoteDrawer: boolean = false;
  isLoadingNote: boolean = false;
  isSavingNote: boolean = false;
  isEditingNote: boolean = false;
  releaseNoteContent: string = '';
  releaseNoteUpdatedAt?: string;
  releaseNoteUpdatedBy?: string;
  noteFeedbackMessage: string = '';
  noteErrorMessage: string = '';

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }, { 'header': 3 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ]
  };

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      // 1. Fetch available versions
      this.appVersionsList = await this.photoboothService.getAppReleases();
      if (this.appVersionsList.length > 0) {
        this.selectedVersion = this.appVersionsList[0];
      }

      // 2. Fetch current device status
      if (this.deviceId) {
        const deviceRef = doc(this.firestore, 'devices', this.deviceId);
        const snap = await getDoc(deviceRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data['appVersion']) this.currentAppVersion = data['appVersion'];
          this.deviceTargetVersion = data['targetAppVersion'];
          this.deviceUpdateNeeded = data['appUpdateNeeded'] === true;
        }
      }
    } catch (err: any) {
      console.error('Error loading app release modal data', err);
      this.errorMessage = 'Failed to load app releases or device data.';
    } finally {
      this.isLoading = false;
    }
  }

  onSelectVersion(event: any) {
    const versionStr = event.target.value;
    this.selectedVersion = this.appVersionsList.find(v => v.version === versionStr) || null;
    if (this.showReleaseNoteDrawer && this.selectedVersion) {
      this.loadReleaseNoteForSelectedVersion();
    }
  }

  async toggleReleaseNotesDrawer(openState?: boolean) {
    if (openState !== undefined) {
      this.showReleaseNoteDrawer = openState;
    } else {
      this.showReleaseNoteDrawer = !this.showReleaseNoteDrawer;
    }

    if (this.showReleaseNoteDrawer && this.selectedVersion) {
      await this.loadReleaseNoteForSelectedVersion();
    }
  }

  async loadReleaseNoteForSelectedVersion() {
    if (!this.selectedVersion?.version) return;

    this.isLoadingNote = true;
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      const verData = await this.photoboothService.getAppReleaseVersion(this.selectedVersion.version);
      if (verData) {
        this.releaseNoteContent = verData.releaseNote || '';
        this.releaseNoteUpdatedAt = verData.releaseNoteUpdatedAt;
        this.releaseNoteUpdatedBy = verData.releaseNoteUpdatedBy;
      } else {
        this.releaseNoteContent = '';
        this.releaseNoteUpdatedAt = undefined;
        this.releaseNoteUpdatedBy = undefined;
      }
    } catch (err: any) {
      console.error('Error loading release note', err);
      this.noteErrorMessage = 'Failed to load release notes for this version.';
    } finally {
      this.isLoadingNote = false;
    }
  }

  startEditNote() {
    this.isEditingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
  }

  cancelEditNote() {
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
    this.loadReleaseNoteForSelectedVersion();
  }

  async saveReleaseNote() {
    if (!this.selectedVersion?.version) return;

    this.isSavingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      await this.photoboothService.saveAppReleaseNote(this.selectedVersion.version, this.releaseNoteContent || '');
      this.releaseNoteUpdatedAt = new Date().toISOString();
      this.noteFeedbackMessage = 'Release note saved successfully!';
      this.isEditingNote = false;
    } catch (err: any) {
      console.error('Error saving release note', err);
      this.noteErrorMessage = `Failed to save release note: ${err.message || err}`;
    } finally {
      this.isSavingNote = false;
    }
  }

  async deleteReleaseNote() {
    if (!this.selectedVersion?.version) return;

    if (!confirm(`Are you sure you want to delete the release note for v${this.selectedVersion.version}?`)) {
      return;
    }

    this.isLoadingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      await this.photoboothService.deleteAppReleaseNote(this.selectedVersion.version);
      this.releaseNoteContent = '';
      this.releaseNoteUpdatedAt = undefined;
      this.releaseNoteUpdatedBy = undefined;
      this.isEditingNote = false;
      this.noteFeedbackMessage = 'Release note deleted.';
    } catch (err: any) {
      console.error('Error deleting release note', err);
      this.noteErrorMessage = `Failed to delete release note: ${err.message || err}`;
    } finally {
      this.isLoadingNote = false;
    }
  }

  getSanitizedNote(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.releaseNoteContent || '');
  }

  async publishToDevice() {
    if (!this.deviceId || !this.selectedVersion) return;

    const forceText = this.forceUpdate ? ' (FORCE UPDATE)' : '';
    const restartText = this.triggerRestart ? ' The machine will also receive a restart command.' : '';
    if (!confirm(`Are you sure you want to release Version ${this.selectedVersion.version}${forceText} to ${this.deviceName}?${restartText}`)) {
      return;
    }

    this.isPublishing = true;
    this.statusMessage = '';
    this.errorMessage = '';

    try {
      await this.photoboothService.releaseAppToDevice(this.deviceId, this.selectedVersion, this.triggerRestart, this.forceUpdate);
      this.statusMessage = `Successfully targeted v${this.selectedVersion.version} for ${this.deviceName}!`;
      this.deviceUpdateNeeded = true;
      this.deviceTargetVersion = this.selectedVersion.version;
      this.released.emit();
    } catch (err: any) {
      console.error('Failed to release app to device', err);
      this.errorMessage = `Error releasing app: ${err.message || err}`;
    } finally {
      this.isPublishing = false;
    }
  }
}
